<template>
  <div class="app-nav-flyout">
    <v-menu
      ref="menu"
      v-model="open"
      right
      offset-x
      :nudge-right="touch ? 8 : 12"
      :min-width="width"
      :max-width="width"
      content-class="app-flyout"
    >
      <template v-slot:activator="{ on, attrs }">
        <slot name="activator" :on="on" :attrs="attrs" :open="open" />
      </template>
      <div class="app-flyout__body" :class="{ 'app-flyout__body--touch': touch }" role="none">
        <p class="app-flyout__title" role="presentation">{{ group.title }}</p>
        <router-link
          v-for="entry in group.children"
          :key="entry.path"
          :to="entry.path"
          class="v-list-item app-flyout__item"
          role="menuitem"
          :aria-current="isActive(entry) ? 'page' : null"
        >{{ entry.title }}</router-link>
      </div>
    </v-menu>
    <v-tooltip
      v-if="tipOn"
      :activator="activatorEl"
      right
      open-delay="400"
      content-class="app-tooltip"
    >
      <span>{{ group.title }}</span>
    </v-tooltip>
  </div>
</template>

<script>
// Flyout einer Navigationsgruppe neben Rail bzw. Touch-Rail (Spezifikation 2.11, gen_r3.py:808–830):
// v-menu right offset-x, Klick öffnet, Klick daneben, Esc und Klick auf einen Eintrag schließen.
// Maus 200 breit mit Einträgen 32, Touch 264 breit mit Einträgen 48; Fläche Radius 6, Einträge Radius 4 und 400;
// aktiver Eintrag als flache getönte Fläche (--tint, hell wie die Fensterfarbe; 600, aria-current) – das Gegenstück
// zum hellen aktiven Punkt (--here) auf der grauen Navigation.
// Slot activator ({ on, attrs, open }): on/attrs an den Rail-Knopf binden.
// tooltip (nur Maus-Rail): Tooltip rechts mit dem Gruppennamen; er hängt über die activator-Prop am Rail-Knopf
// und wird entfernt, solange irgendein Navigations-Popup offen ist (shell.popupOffen), damit er nicht darüber steht.
import { normalizePath } from '../../shell/navigation';
import { notePopup } from '../../shell/shellState';

export default {
  name: 'AppNavFlyout',
  props: {
    group: { type: Object, required: true },
    activePath: { type: String, default: '' },
    touch: { type: Boolean, default: false },
    tooltip: { type: Boolean, default: false }
  },
  data() {
    return { open: false, activatorEl: null };
  },
  computed: {
    width() {
      return this.touch ? 264 : 200;
    },
    tipOn() {
      const touchDensity = !!(this.$dichte && this.$dichte.touch);
      return this.tooltip && !touchDensity && !this.$shell.popupOffen && !!this.activatorEl;
    }
  },
  watch: {
    open(value) {
      notePopup(value);
    }
  },
  beforeDestroy() {
    if (this.open) {
      notePopup(false);
    }
  },
  mounted() {
    // Rail-Knopf aus dem Aktivator-Slot (Element bleibt beim Neurendern dasselbe)
    const nodes = this.$refs.menu && this.$refs.menu.activatorNode;
    this.activatorEl = nodes && nodes[0] && nodes[0].elm ? nodes[0].elm : null;
  },
  methods: {
    isActive(entry) {
      return normalizePath(entry.path) === this.activePath;
    }
  }
};
</script>

<style scoped>
/* Hülle um den Rail-Knopf (v-menu und v-tooltip selbst sind unsichtbar): Flex, damit keine Zeilenhöhe entsteht */
.app-nav-flyout {
  display: flex;
}
/* Menüfläche: --surface-raised (hell weiß), Kante --line, Radius 6 wie die übrigen Menüs, Schatten wie Menüs,
   Polster 4 */
.v-menu__content.app-flyout {
  box-sizing: border-box;
  padding: 4px;
  background: var(--surface-raised);
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  box-shadow: var(--sh-menu);
  color: var(--ink);
  font-family: var(--font);
}
.app-flyout__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
p.app-flyout__title {
  margin: 0;
  padding: 8px 8px 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: var(--ink-3);
}
.app-flyout__body--touch p.app-flyout__title {
  padding: 8px 16px 4px;
}
/* Einträge: überschreibt die Grundwerte von .v-list-item (Klasse nur für die Pfeiltasten von v-menu) */
.app-flyout__body .app-flyout__item {
  flex: none;
  display: flex;
  align-items: center;
  height: 32px;
  min-height: 32px;
  box-sizing: border-box;
  padding: 0 7px;
  border: 1px solid transparent;
  border-radius: var(--r-inner);
  background: transparent;
  color: var(--ink-2);
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  letter-spacing: normal;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.app-flyout__body--touch .app-flyout__item {
  height: 48px;
  min-height: 48px;
  padding: 0 15px;
  font-size: 14px;
  line-height: 20px;
}
.app-flyout__body .app-flyout__item::after {
  content: none;
}
/* Zustände wie in den Menüs: Hover --hov (nur Maus), aktuell --tint, gedrückt --press */
:where(html:not(.dichte-touch)) .app-flyout__body .app-flyout__item:hover {
  background: var(--hov);
}
.app-flyout__body .app-flyout__item[aria-current="page"] {
  background: var(--tint);
  color: var(--ink);
  font-weight: 700;
}
.app-flyout__body .app-flyout__item:active {
  background: var(--press);
}
.app-flyout__body .app-flyout__item:focus {
  outline: none;
}
.app-flyout__body .app-flyout__item:focus-visible,
.app-flyout__body .app-flyout__item.v-list-item--highlighted {
  box-shadow: inset 0 0 0 2px var(--link);
}
</style>
